<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const saveError = ref('');
const discardRef = ref(null);
const submitting = ref(false);

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const contact = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleDialogConfirm = async () => {
  if (
    !contact.value ||
    contactsFormRef.value?.isFormInvalid ||
    submitting.value ||
    isCreatingContact.value
  )
    return;
  submitting.value = true;
  saveError.value = '';
  emit('create', contact.value);
};

const onSuccess = () => {
  submitting.value = false;
  saveError.value = '';
  contactsFormRef.value?.resetForm();
  contact.value = null;
  dialogRef.value.close();
};

const discard = () => {
  contactsFormRef.value?.resetForm();
  contact.value = null;
  saveError.value = '';
  discardRef.value?.close();
  dialogRef.value.close();
};
const closeDialog = () => {
  if (submitting.value || isCreatingContact.value) return;
  if (isWorkspaceV2.value && contactsFormRef.value?.isDirty) {
    discardRef.value?.open();
    return;
  }
  dialogRef.value.close();
};
const onError = message => {
  submitting.value = false;
  saveError.value = message;
};

defineExpose({ dialogRef, contactsFormRef, onSuccess, onError });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :confirm-before-close="isWorkspaceV2 || submitting"
    @request-close="closeDialog"
    @confirm="handleDialogConfirm"
  >
    <fieldset
      class="m-0 min-w-0 border-0 p-0"
      :disabled="submitting || isCreatingContact"
    >
      <ContactsForm
        ref="contactsFormRef"
        is-new-contact
        @update="createNewContact"
      />
    </fieldset>
    <p
      v-if="isWorkspaceV2 && saveError"
      role="alert"
      class="text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :disabled="
            contactsFormRef?.isFormInvalid || submitting || isCreatingContact
          "
          :is-loading="isCreatingContact"
        />
      </div>
    </template>
  </Dialog>
  <Dialog
    ref="discardRef"
    type="alert"
    :title="t('WORKSPACE_V2.CONTACT_EDITOR.DISCARD_TITLE')"
    :description="t('WORKSPACE_V2.CONTACT_EDITOR.DISCARD_DESCRIPTION')"
    :confirm-button-label="t('WORKSPACE_V2.TASK_EDITOR.DISCARD')"
    :cancel-button-label="t('WORKSPACE_V2.TASK_EDITOR.KEEP_EDITING')"
    @confirm="discard"
  />
</template>
